// NearbyResultsMap — Leaflet map for the GeoLink Nearby Bookings page.
//
// Renders:
//   - the OSM tile layer
//   - a translucent radius circle around the search centre
//   - blue CircleMarkers for booking results
//   - orange CircleMarkers for skip-job results
//   - per-marker popups; click handlers route back to the parent so the
//     parent can highlight the matching row in the results table
//
// DriverMapView grows a `radiusOverlay` prop for the live-driver view
// but this page renders its own map so it can colour the result markers
// by source type — outside DriverMapView's contract.

import { useEffect } from 'react';
import {
  MapContainer,
  TileLayer,
  Circle,
  CircleMarker,
  Popup,
  useMap,
} from 'react-leaflet';
import type { NearbyBookingDto, NearbySkipJobDto } from '../../../api/geolink';
import styles from './NearbyResultsMap.module.css';

export interface NearbyResultsMapProps {
  centre: [number, number];
  radiusMiles: number;
  bookings: NearbyBookingDto[];
  skipJobs: NearbySkipJobDto[];
  highlightedEncId?: string | null;
  onSelect?: (kind: 'booking' | 'skip_job', encId: string) => void;
}

const DEFAULT_ZOOM = 11;

function FitToCircle({ centre, radiusMiles }: { centre: [number, number]; radiusMiles: number }) {
  const map = useMap();
  useEffect(() => {
    // 1 mile = 0.0145 deg lat as a rough fit envelope. Multiply by 1.2
    // for a small margin around the circle.
    const padDeg = radiusMiles * 0.0145 * 1.2;
    map.fitBounds([
      [centre[0] - padDeg, centre[1] - padDeg * 1.6],
      [centre[0] + padDeg, centre[1] + padDeg * 1.6],
    ]);
  }, [map, centre, radiusMiles]);
  return null;
}

export function NearbyResultsMap({
  centre,
  radiusMiles,
  bookings,
  skipJobs,
  highlightedEncId,
  onSelect,
}: NearbyResultsMapProps) {
  return (
    <div className={styles.wrapper}>
      <MapContainer center={centre} zoom={DEFAULT_ZOOM} className={styles.map} scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitToCircle centre={centre} radiusMiles={radiusMiles} />
        <Circle
          center={centre}
          radius={radiusMiles * 1609.34}
          pathOptions={{ color: '#3b82f6', fillOpacity: 0.05, weight: 1.5 }}
        />

        {bookings.map((b) => {
          const isActive = highlightedEncId === b.enc_id;
          return (
            <CircleMarker
              key={`b-${b.enc_id}`}
              center={[b.latitude, b.longitude]}
              radius={isActive ? 10 : 7}
              pathOptions={{
                color: isActive ? '#1d4ed8' : '#2563eb',
                fillColor: '#3b82f6',
                fillOpacity: 0.85,
                weight: isActive ? 3 : 1.5,
              }}
              eventHandlers={
                onSelect ? { click: () => onSelect('booking', b.enc_id) } : undefined
              }
            >
              <Popup>
                <p className={styles.popupName}>
                  {b.booking_number} · {b.customer_name}
                </p>
                <p className={styles.popupMeta}>
                  {b.date} · {b.timeframe}
                  <br />
                  {b.postcode} · {b.distance_miles.toFixed(1)} mi
                </p>
              </Popup>
            </CircleMarker>
          );
        })}

        {skipJobs.map((s) => {
          const isActive = highlightedEncId === s.enc_id;
          return (
            <CircleMarker
              key={`s-${s.enc_id}`}
              center={[s.latitude, s.longitude]}
              radius={isActive ? 10 : 7}
              pathOptions={{
                color: isActive ? '#c2410c' : '#ea580c',
                fillColor: '#f97316',
                fillOpacity: 0.85,
                weight: isActive ? 3 : 1.5,
              }}
              eventHandlers={
                onSelect ? { click: () => onSelect('skip_job', s.enc_id) } : undefined
              }
            >
              <Popup>
                <p className={styles.popupName}>
                  {s.job_number} · {s.customer_name}
                </p>
                <p className={styles.popupMeta}>
                  {s.date ?? '—'} · {s.timeframe ?? '—'}
                  <br />
                  {s.postcode} · {s.distance_miles.toFixed(1)} mi
                </p>
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>

      <div className={styles.legend} aria-hidden>
        <span>
          <span className={`${styles.swatch} ${styles.swatchBooking}`} /> Bookings
        </span>
        <span>
          <span className={`${styles.swatch} ${styles.swatchSkip}`} /> Skip jobs
        </span>
      </div>
    </div>
  );
}
